<script lang="ts">
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import type { CloudRecording } from '../types/cloud-recordings';
	import type { Device } from 'src/types/Device';
	import { onDestroy, onMount } from 'svelte';
	import { getCloudRecordings } from 'src/services/cam-cloud/recordings';
	import { Container, Text, Loader, Stack } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	import CameraCloudStoragePromoBanner from 'src/components/Settings/CameraCloudStorage/CameraCloudStoragePromoBanner.svelte';
	import RecordingsListItem from './RecordingsTab/RecordingsListItem.svelte';
	import NoResults from './RecordingsTab/NoResults.svelte';
	import RecordingSetupCta from './RecordingsTab/RecordingSetupCta.svelte';
	import RecordingsLoadError from './RecordingsTab/RecordingsLoadError.svelte';
	import { onCameraRecordingChange } from 'src/components/Camera/helpers/camera-thumbnail-watch';
	import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
	import { needsEncryptionSetup } from 'src/components/Camera/helpers/recording-key-management';
	import {
		isCameraEncryptionConfigured,
		isCameraEncryptionSupported,
	} from 'src/components/Camera/helpers/camera-features';
	import { devices_status } from 'src/stores';
	import { real_id } from 'src/helpers/interface_helpers';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import { hasCameraCloudStorage } from 'src/helpers/camera_cloud_storage_helpers';

	export let device: Device;
	export let deviceId: string;

	const RECORDINGS_LIMIT = 3;

	let recordings: CloudRecording[] = [];
	let isLoading = true;
	let loadError = false;
	/** `undefined` until the first check resolves, so neither the CTA nor the promo flashes. */
	let showSetupCta: boolean | undefined;
	let promoVisible = false;
	let setupCheckToken = 0;

	const refreshSetupState = async (currentDevice: Device) => {
		// The backup lookup can hit the network, so an earlier, slower check must
		// not land after a newer one and revive a stale answer.
		const token = ++setupCheckToken;
		const needsSetup = await needsEncryptionSetup(currentDevice);
		if (token === setupCheckToken) showSetupCta = needsSetup;
	};

	// Without a slot no recording will ever land here, so the empty state has to say so.
	$: hasCloudStorage =
		!!$camera_cloud_storage.slotAssignments && hasCameraCloudStorage(deviceId ?? '');

	$: cameraStatus = $devices_status[real_id(device?.id)];
	// The camera is the source of truth for whether it holds a key, and that only
	// flips once provisioning lands as a status push — so follow the status, not
	// just the local backup. Gate on the flags rather than on `cameraStatus`: the
	// status object is replaced on every push, and re-checking hits the network
	// whenever no backup is cached locally.
	$: encryptionSupported = isCameraEncryptionSupported(device, cameraStatus);
	$: encryptionConfigured = isCameraEncryptionConfigured(device, cameraStatus);
	$: {
		encryptionSupported;
		encryptionConfigured;
		$camera_storage_keys;
		refreshSetupState(device);
	}

	const loadRecordings = async () => {
		// Recordings belong to the owner's account, so a guest has none to list.
		if (device?.shared) {
			isLoading = false;
			return;
		}
		loadError = false;
		try {
			const result = await getCloudRecordings(deviceId, { limit: RECORDINGS_LIMIT, offset: 0 });
			recordings = result.items as CloudRecording[];
		} catch (error) {
			console.log('Failed to load recordings', error);
			loadError = true;
		} finally {
			isLoading = false;
		}
	};

	const handleRetry = () => {
		loadError = false;
		loadRecordings();
	};

	const navigateToSetting = (setting, subSetting, params) => {
		routeToDeviceSettings.setTarget(deviceId, setting, subSetting, params);
	};

	const handleSelect = (event) => {
		const recording = event.detail;
		navigateToSetting('camera_recordings', 'view', recording);
	};

	onMount(loadRecordings);

	const unsubscribe = onCameraRecordingChange(deviceId, () => loadRecordings());
	onDestroy(unsubscribe);
</script>

<Text styleType="body" class="text-bold mt-m"
	>{$format('last_recordings_title', { default: 'Last Recordings' })}</Text
>
{#if device?.shared}
	<Container class="pb-l">
		<NoResults
			noResultsFound={{
				icon: 'fa-light fa-lock',
				title: $format('recordings_list_shared_device', { default: 'Shared device' }),
				description: $format('recordings_list_shared_device_description', {
					default: 'This camera is shared with you, so its recordings stay with its owner.',
				}),
			}}
		/>
	</Container>
{:else if isLoading}
	<div class="d-flex justify-content-center width-full p-m">
		<Loader />
	</div>
{:else if loadError}
	<Container class="pb-l">
		<RecordingsLoadError on:retry={handleRetry} />
	</Container>
{:else if showSetupCta && recordings.length === 0}
	<Container class="pb-l">
		<RecordingSetupCta {deviceId} variant="empty" />
	</Container>
{:else if recordings.length > 0}
	{#if showSetupCta}
		<RecordingSetupCta {deviceId} variant="banner" class="mt-m" />
	{/if}
	<Stack gap="s">
		{#each recordings as recording}
			<RecordingsListItem {recording} selected={false} variant="simple" on:select={handleSelect} />
		{/each}
	</Stack>
{:else if !promoVisible}
	<Container class="pb-l">
		<!-- Without the promo banner, this empty state is the only path left to Cloud Storage. -->
		<NoResults
			noResultsFound={{
				icon: 'fa-light fa-camera-cctv',
				title: $format('recordings_list_no_recordings', { default: 'No events recorded yet' }),
				description: hasCloudStorage
					? $format('recordings_list_no_cloud_recordings_description', {
							default: 'Cloud Recordings will appear here when event is detected.',
						})
					: $format('recordings_list_no_cloud_storage_description', {
							default:
								'Cloud Recordings are not active for this camera. Activate Cloud Storage to start recording events.',
						}),
			}}
			showCloudStorageLink={showSetupCta === false}
		/>
	</Container>
{/if}

<!-- The setup CTA above already asks for the user's attention; one ask at a time. -->
{#if showSetupCta === false}
	<CameraCloudStoragePromoBanner {deviceId} bind:visible={promoVisible} />
{/if}
